import { useEffect, useRef } from 'react'
import { useTheme } from '../contexts/ThemeContext'

type Intensity = 'low' | 'med' | 'high'

interface CircuitBackgroundProps {
  /** How much current flows. Default 'low' — the calibrated, ship-it-everywhere level. */
  intensity?: Intensity
}

const CFG: Record<Intensity, { n: number; base: number }> = {
  low: { n: 5, base: 0.7 },
  med: { n: 10, base: 1 },
  high: { n: 18, base: 1.35 },
}

// rgb triples per theme. Kept faint so the layer reads as atmosphere, never as content.
function palette(dark: boolean) {
  return dark
    ? { trace: '91,157,255', head: '120,190,255', head2: '255,181,71', baseA: 0.09 }
    : { trace: '100,116,139', head: '245,158,11', head2: '37,99,235', baseA: 0.06 }
}

interface Pt { x: number; y: number }
interface Trace { pts: Pt[]; seg: number[]; len: number }
interface Pulse { tr: Trace; d: number; sp: number; alt: boolean }

/**
 * Ambient circuit-board layer: faint PCB traces with pulses of current flowing
 * along them. Fixed behind all content, one cached canvas, pauses when the tab
 * is hidden, and freezes to static traces under prefers-reduced-motion.
 *
 * It draws nothing opaque — it relies on the page's own ground color showing
 * through, so the shell (Layout) must be transparent for it to be visible.
 */
export function CircuitBackground({ intensity = 'low' }: CircuitBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const { theme } = useTheme()
  const darkRef = useRef(theme === 'dark')
  darkRef.current = theme === 'dark'

  // A redraw signal the animation loop watches, bumped when the theme flips.
  const redrawRef = useRef(0)
  useEffect(() => { redrawRef.current += 1 }, [theme])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return // jsdom / unsupported

    const reduce =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    let W = 0, H = 0, DPR = 1
    let traces: Trace[] = []
    let pulses: Pulse[] = []
    const layer = document.createElement('canvas')
    const lctx = layer.getContext('2d')
    if (!lctx) return
    let lastRedraw = -1

    const snap = (v: number, g: number) => Math.round(v / g) * g
    const rnd = (a: number, b: number) => a + Math.random() * (b - a)

    function makePath(): Trace {
      const g = 44
      const horizontal = Math.random() < 0.62
      const pts: Pt[] = []
      let x: number, y: number, dir: 'h' | 'v'
      if (horizontal) { x = 0; y = snap(rnd(0, H), g); dir = 'h' }
      else { x = snap(rnd(0, W), g); y = 0; dir = 'v' }
      pts.push({ x, y })
      let guard = 0
      while (guard++ < 40) {
        if (dir === 'h') x += g * Math.round(rnd(2, 7))
        else y += (Math.random() < 0.72 ? 1 : -1) * g * Math.round(rnd(2, 6))
        x = Math.max(0, Math.min(W, x)); y = Math.max(0, Math.min(H, y))
        pts.push({ x, y })
        dir = dir === 'h' ? 'v' : 'h'
        if (x >= W || y >= H) break
      }
      let len = 0
      const seg: number[] = []
      for (let i = 1; i < pts.length; i++) {
        const d = Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y)
        seg.push(len); len += d
      }
      seg.push(len)
      return { pts, seg, len }
    }

    function pointAt(tr: Trace, dist: number): Pt {
      const { pts, seg } = tr
      for (let i = 1; i < pts.length; i++) {
        if (dist <= seg[i]) {
          const t = (dist - seg[i - 1]) / Math.max(1, seg[i] - seg[i - 1])
          return {
            x: pts[i - 1].x + (pts[i].x - pts[i - 1].x) * t,
            y: pts[i - 1].y + (pts[i].y - pts[i - 1].y) * t,
          }
        }
      }
      const p = pts[pts.length - 1]
      return { x: p.x, y: p.y }
    }

    function drawLayer() {
      const c = palette(darkRef.current)
      layer.width = W * DPR; layer.height = H * DPR
      lctx!.setTransform(DPR, 0, 0, DPR, 0, 0)
      lctx!.clearRect(0, 0, W, H)
      lctx!.lineJoin = 'round'; lctx!.lineCap = 'round'
      const a = c.baseA * CFG[intensity].base
      for (const tr of traces) {
        lctx!.beginPath()
        tr.pts.forEach((p, i) => (i ? lctx!.lineTo(p.x, p.y) : lctx!.moveTo(p.x, p.y)))
        lctx!.strokeStyle = `rgba(${c.trace},${a})`; lctx!.lineWidth = 1.4; lctx!.stroke()
        for (const e of [tr.pts[0], tr.pts[tr.pts.length - 1]]) {
          lctx!.beginPath(); lctx!.arc(e.x, e.y, 2.4, 0, 7)
          lctx!.fillStyle = `rgba(${c.trace},${a * 1.7})`; lctx!.fill()
        }
      }
    }

    function genTraces() {
      const count = Math.max(6, Math.round((W * H) / 95000))
      traces = []
      for (let i = 0; i < count; i++) traces.push(makePath())
    }

    function resize() {
      W = window.innerWidth; H = window.innerHeight
      DPR = Math.min(window.devicePixelRatio || 1, 2)
      canvas!.width = W * DPR; canvas!.height = H * DPR
      ctx!.setTransform(DPR, 0, 0, DPR, 0, 0)
      genTraces(); drawLayer(); lastRedraw = redrawRef.current; pulses = []
    }

    let last = 0
    let raf = 0
    const target = CFG[intensity].n

    function frame(t: number) {
      const dt = Math.min(0.05, (t - last) / 1000 || 0); last = t
      if (redrawRef.current !== lastRedraw) { drawLayer(); lastRedraw = redrawRef.current }
      ctx!.clearRect(0, 0, W, H)
      ctx!.drawImage(layer, 0, 0, W, H)
      const c = palette(darkRef.current)
      if (pulses.length < target && Math.random() < 0.14 && traces.length) {
        const tr = traces[(Math.random() * traces.length) | 0]
        pulses.push({ tr, d: 0, sp: rnd(60, 130), alt: Math.random() < 0.22 })
      }
      for (let i = pulses.length - 1; i >= 0; i--) {
        const p = pulses[i]; p.d += p.sp * dt
        if (p.d > p.tr.len) { pulses.splice(i, 1); continue }
        const col = p.alt ? c.head2 : c.head
        const tailLen = 46, steps = 9
        for (let s = 1; s <= steps; s++) {
          const dd = p.d - (s / steps) * tailLen; if (dd < 0) break
          const pt = pointAt(p.tr, dd)
          ctx!.beginPath(); ctx!.arc(pt.x, pt.y, 1.4 * (1 - s / steps) + 0.4, 0, 7)
          ctx!.fillStyle = `rgba(${col},${(1 - s / steps) * 0.5})`; ctx!.fill()
        }
        ctx!.save(); ctx!.shadowColor = `rgba(${col},0.9)`; ctx!.shadowBlur = 10
        ctx!.beginPath(); ctx!.arc(pointAt(p.tr, p.d).x, pointAt(p.tr, p.d).y, 2.2, 0, 7)
        ctx!.fillStyle = `rgba(${col},0.95)`; ctx!.fill(); ctx!.restore()
      }
      raf = requestAnimationFrame(frame)
    }

    function drawStatic() {
      if (redrawRef.current !== lastRedraw) { drawLayer(); lastRedraw = redrawRef.current }
      ctx!.clearRect(0, 0, W, H); ctx!.drawImage(layer, 0, 0, W, H)
    }
    function start() { if (!raf && !reduce) { last = 0; raf = requestAnimationFrame(frame) } }
    function stop() { if (raf) { cancelAnimationFrame(raf); raf = 0 } drawStatic() }

    function onVisibility() { document.hidden ? stop() : start() }
    function onResize() { resize(); if (!document.hidden) start() }

    resize()
    if (reduce) drawStatic()
    else start()
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('resize', onResize)

    return () => {
      if (raf) cancelAnimationFrame(raf)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('resize', onResize)
    }
  }, [intensity])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="fixed inset-0 h-full w-full pointer-events-none z-[-1]"
    />
  )
}
